/**
 * Centralized storage service for localStorage operations
 * Single source of truth for all storage-related functionality
 */
class StorageService {
  private readonly AUTH_KEYS = {
    ACCESS_TOKEN: "ova_access_token",
    REFRESH_TOKEN: "ova_refresh_token",
    SID: "ova_sid",
    USER_ID: "ova_user_id",
    EMAIL: "user_email",
    PLATFORM_PROFILE: "platform_profile",
  } as const;

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(this.AUTH_KEYS.ACCESS_TOKEN);
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.AUTH_KEYS.REFRESH_TOKEN);
  }

  /**
   * Get session ID (sid)
   */
  getSid(): string | null {
    return localStorage.getItem(this.AUTH_KEYS.SID);
  }

  /**
   * Get authentication token (alias for getAccessToken for backward compatibility)
   */
  getToken(): string | null {
    return this.getAccessToken();
  }

  /**
   * Get all authentication data
   */
  getAuthData() {
    return {
      accessToken: this.getAccessToken(),
      refreshToken: this.getRefreshToken(),
      sid: this.getSid(),
      userId: localStorage.getItem(this.AUTH_KEYS.USER_ID),
      email: localStorage.getItem(this.AUTH_KEYS.EMAIL),
      platformProfile: this.getPlatformProfile(),
    };
  }

  /**
   * Save tokens (access_token, refresh_token, sid)
   */
  saveTokens(data: {
    access_token: string;
    refresh_token: string;
    sid: string;
  }): void {
    localStorage.setItem(this.AUTH_KEYS.ACCESS_TOKEN, data.access_token);
    localStorage.setItem(this.AUTH_KEYS.REFRESH_TOKEN, data.refresh_token);
    localStorage.setItem(this.AUTH_KEYS.SID, data.sid);
  }

  /**
   * Save authentication data to localStorage
   */
  saveAuthData(data: {
    access_token?: string;
    refresh_token?: string;
    sid?: string;
    token?: string;
    id?: string;
    email?: string;
    platform_profile?: any;
  }): void {
    // Support new v2 token structure
    if (data.access_token && data.refresh_token && data.sid) {
      this.saveTokens({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        sid: data.sid,
      });
    }
    // Backward compatibility with old token structure
    else if (data.token) {
      localStorage.setItem(this.AUTH_KEYS.ACCESS_TOKEN, data.token);
    }

    if (data.id) {
      localStorage.setItem(this.AUTH_KEYS.USER_ID, data.id);
    }

    if (data.email) {
      localStorage.setItem(this.AUTH_KEYS.EMAIL, data.email);
    }

    if (data.platform_profile) {
      localStorage.setItem(
        this.AUTH_KEYS.PLATFORM_PROFILE,
        JSON.stringify(data.platform_profile)
      );
    }
  }

  /**
   * Clear all authentication data
   */
  clearAuthData(): void {
    Object.values(this.AUTH_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Get platform profile with JSON parsing
   */
  private getPlatformProfile(): any | null {
    const profile = localStorage.getItem(this.AUTH_KEYS.PLATFORM_PROFILE);
    try {
      return profile ? JSON.parse(profile) : null;
    } catch {
      return null;
    }
  }
}

// Export singleton instance
export const storageService = new StorageService();
